import { useLayoutEffect, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

export interface ModuleMenuItem { label: string; }

/** Render outside the horizontally scrolling navbar so overflow cannot clip the menu. */
export function ModuleDropdown<T extends ModuleMenuItem>({ anchorId, label, items, onSelect, onClose }: {
  anchorId: string;
  label: string;
  items: T[];
  onSelect: (item: T) => void;
  onClose: () => void;
}) {
  const menuRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ left: 8, top: 8, maxHeight: 300 });
  useLayoutEffect(() => {
    const update = () => {
      const anchor = document.getElementById(anchorId);
      if (!anchor) return;
      const rect = anchor.getBoundingClientRect();
      const height = window.innerHeight;
      setPosition({
        left: Math.max(8, Math.min(rect.left, window.innerWidth - 228)),
        top: Math.max(8, Math.min(rect.bottom + 4, height - 80)),
        maxHeight: Math.max(64, height - Math.min(rect.bottom + 4, height - 80) - 8)
      });
    };
    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => { window.removeEventListener("resize", update); window.removeEventListener("scroll", update, true); };
  }, [anchorId]);
  useEffect(() => { menuRef.current?.querySelector<HTMLButtonElement>("button")?.focus(); }, [anchorId]);
  useEffect(() => {
    const outside = (event: PointerEvent) => {
      const target = event.target as Node;
      if (!menuRef.current?.contains(target) && !document.getElementById(anchorId)?.contains(target)) onClose();
    };
    document.addEventListener("pointerdown", outside);
    return () => document.removeEventListener("pointerdown", outside);
  }, [anchorId, onClose]);
  return createPortal(<div id={`${anchorId}-menu`} ref={menuRef} className="module-nav-dropdown module-nav-dropdown-portal" role="menu" aria-label={label} style={position} onKeyDown={event => {
    const buttons = Array.from(menuRef.current?.querySelectorAll<HTMLButtonElement>("button") ?? []);
    const current = buttons.indexOf(document.activeElement as HTMLButtonElement);
    if (["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) {
      event.preventDefault();
      const next = event.key === "Home" ? 0 : event.key === "End" ? buttons.length - 1 : (current + (event.key === "ArrowDown" ? 1 : -1) + buttons.length) % buttons.length;
      buttons[next]?.focus();
    } else if (event.key === "Escape" || event.key === "Tab") {
      if (event.key === "Escape") event.preventDefault();
      document.getElementById(anchorId)?.focus(); onClose();
    }
  }}>
    {items.map(item => <button key={item.label} role="menuitem" tabIndex={-1} onClick={() => onSelect(item)} type="button">{item.label}</button>)}
  </div>, document.body);
}
